/* ============================================================
   Modulo: Hero Rail

   The rail scrolls natively — this only wires the arrows, their
   disabled state, the ticker's clone-to-fill measurement, and the
   ticker pause control (WCAG SC 2.2.2, Level A).

   Three measured lessons are encoded here; do not "simplify" them:
   1. The marquee cycle is the PITCH between repeats, not one set's
      scrollWidth — scrollWidth counts the gaps INSIDE a set but not
      the one between set N and N+1.
   2. Measure inside document.fonts.ready. Webfonts land after a
      deferred script, and measuring first desyncs the loop so the
      band visibly snaps once per cycle.
   3. scrollBy's `left` is a PHYSICAL delta. A spec-compliant RTL
      scroller runs 0 -> -max, so an unflipped Next is a no-op there
      while the disabled-state sync (which uses Math.abs) still looks
      correct — silent breakage.
   ============================================================ */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;

  var REDUCE = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  /* ─── ticker ─── */

  function unitLength(track) {
    var n = parseInt(track.getAttribute('data-unit'), 10);
    return isNaN(n) || n < 1 ? 0 : n;
  }

  function buildTicker(ticker) {
    var track = ticker.querySelector('[data-modulo-hrl-track]');
    if (!track) return;

    var unit = unitLength(track);
    if (!unit) return;

    // Drop previous clones and re-measure from scratch. Topping up is not
    // enough: a phone rotated 390 -> 844 crosses both the responsive
    // font-size and the gap breakpoints, invalidating the old cycle.
    var kids = Array.prototype.slice.call(track.children);
    for (var i = kids.length - 1; i >= unit; i--) track.removeChild(kids[i]);
    track.removeAttribute('data-modulo-hrl-ready');
    track.style.removeProperty('--modulo-hrl-cycle');

    if (REDUCE.matches) return;

    var originals = Array.prototype.slice.call(track.children);
    if (originals.length < unit) return;

    // Pitch between repeats: clone one set, measure, then size the fill.
    appendSet(track, originals);
    var cycle = track.children[unit].offsetLeft - track.children[0].offsetLeft;
    if (!(cycle > 0)) return;

    var viewport = ticker.offsetWidth || document.documentElement.clientWidth;
    var copies = Math.max(2, Math.ceil((viewport + cycle) / cycle));
    for (var c = 1; c < copies; c++) appendSet(track, originals);

    track.style.setProperty('--modulo-hrl-cycle', cycle + 'px');
    track.setAttribute('data-modulo-hrl-ready', '1');
  }

  function appendSet(track, originals) {
    for (var i = 0; i < originals.length; i++) {
      var clone = originals[i].cloneNode(true);
      // Without this the same phrases are announced once per copy.
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      track.appendChild(clone);
    }
  }

  function wirePause(ticker) {
    var btn = ticker.querySelector('[data-modulo-hrl-pause]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var paused = ticker.classList.toggle('is-paused');
      btn.setAttribute('aria-pressed', String(paused));
      btn.setAttribute('aria-label', paused
        ? btn.getAttribute('data-label-play')
        : btn.getAttribute('data-label-pause'));
      var icon = btn.querySelector('[data-modulo-hrl-pause-icon]');
      if (!icon) return;
      // built with DOM APIs, never innerHTML
      while (icon.firstChild) icon.removeChild(icon.firstChild);
      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', paused ? 'M8 5v14l11-7z' : 'M7 5h4v14H7zM13 5h4v14h-4z');
      icon.appendChild(path);
    });
  }

  /* ─── rail ─── */

  function wireRail(root) {
    var rail = root.querySelector('[data-modulo-hrl-rail]');
    if (!rail) return;
    var prev = root.querySelector('[data-modulo-hrl-prev]');
    var next = root.querySelector('[data-modulo-hrl-next]');

    function sync() {
      var max = rail.scrollWidth - rail.clientWidth;
      // Math.abs: a spec-compliant RTL scroller runs 0 -> -max, so probe the
      // magnitude rather than sniffing the direction.
      var pos = Math.abs(rail.scrollLeft);
      if (prev) prev.disabled = pos <= 2;
      if (next) next.disabled = pos >= max - 2;
    }

    function page(dir) {
      var panel = rail.querySelector('.modulo-hrl__panel');
      if (!panel) return;
      var gap = parseFloat(window.getComputedStyle(rail).columnGap) || 0;
      var step = panel.getBoundingClientRect().width + gap;
      var rtl = window.getComputedStyle(rail).direction === 'rtl';
      rail.scrollBy({
        left: (rtl ? -1 : 1) * dir * step,
        // Live query, not a load-time boolean: the `behavior` option is
        // consulted first and only 'auto' defers to CSS scroll-behavior.
        behavior: REDUCE.matches ? 'auto' : 'smooth'
      });
    }

    if (prev) prev.addEventListener('click', function () { page(-1); });
    if (next) next.addEventListener('click', function () { page(1); });

    var raf = null;
    rail.addEventListener('scroll', function () {
      if (raf) window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(sync);
    });

    root._moduloHrlSync = sync;
    sync();
  }

  /* ─── init ─── */

  function init(root) {
    if (!root || root.dataset.moduloHrlReady === '1') return;
    root.dataset.moduloHrlReady = '1';

    wireRail(root);

    var ticker = root.querySelector('[data-modulo-hrl-ticker]');
    if (ticker) {
      wirePause(ticker);
      // Webfonts land after a deferred script; measuring first desyncs the loop.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { buildTicker(ticker); });
      } else {
        buildTicker(ticker);
      }
    }
  }

  function boot(scope) {
    var els = (scope || document).querySelectorAll('[data-modulo-hrl]');
    for (var i = 0; i < els.length; i++) init(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // An identical <script src> still executes once per tag, so document- and
  // window-level listeners are wired exactly once.
  if (!window.moduloHrlWired) {
    window.moduloHrlWired = true;

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        var roots = document.querySelectorAll('[data-modulo-hrl]');
        for (var i = 0; i < roots.length; i++) {
          var ticker = roots[i].querySelector('[data-modulo-hrl-ticker]');
          if (ticker) buildTicker(ticker);
          if (roots[i]._moduloHrlSync) roots[i]._moduloHrlSync();
        }
      }, 180);
    });

    // Live query, so toggling Reduce motion mid-session takes effect instead
    // of leaving clones behind for the CSS to wrap.
    if (REDUCE.addEventListener) {
      REDUCE.addEventListener('change', function () {
        var tickers = document.querySelectorAll('[data-modulo-hrl-ticker]');
        for (var i = 0; i < tickers.length; i++) buildTicker(tickers[i]);
      });
    }

    document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  }
})();
